import React from 'react';
import { HolidayCoverageEmployee, HolidayCoverageRow, HolidayCoverageSchedule } from '../types/holidayCoverage';

function formatUsual(row: HolidayCoverageRow) {
  if (row.usualCount == null) return '—';
  const value = Number(row.usualCount);
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
  if (row.usualMin != null && row.usualMax != null && row.usualMin !== row.usualMax) {
    return `${text} (${row.usualMin}–${row.usualMax})`;
  }
  return text;
}

function scheduleNote(schedule: HolidayCoverageSchedule, weekday: string) {
  const sample = schedule.historySampleCount;
  const usual = sample > 0
    ? `Usual shifts are the typical count from the last ${sample} rostered ${weekday}${sample === 1 ? '' : 's'} (public holidays skipped).`
    : `No earlier rostered ${weekday}s in the last 26 weeks, so there is no usual pattern.`;
  const scheduled = schedule.rotaBuilt
    ? 'Scheduled shifts are the rota for this date.'
    : 'The rota for this date is not built yet, so scheduled shifts are blank.';
  const pending = schedule.otherPendingCount > 0
    ? `${schedule.otherPendingCount} other pending request${schedule.otherPendingCount === 1 ? '' : 's'} on this schedule ${schedule.otherPendingCount === 1 ? 'is' : 'are'} not counted as off.`
    : '';
  return [usual, scheduled, pending].filter(Boolean).join(' ');
}

function IfApprovedCell({ row }: { row: HolidayCoverageRow }) {
  if (row.shortBecauseOfApproval) {
    return (
      <span className="font-medium text-amber-900" title="Approving this leave leaves the shift short">
        {row.ifApprovedCount}
        <span className="block text-[10px] font-normal">Short by {row.shortBy}</span>
      </span>
    );
  }
  if (row.shortBy > 0) {
    return (
      <span className="text-amber-800" title="This shift is already short before this decision">
        {row.ifApprovedCount}
        <span className="block text-[10px]">Already short by {row.shortBy}</span>
      </span>
    );
  }
  if (!row.applicantSuitable) {
    return (
      <span title="This employee’s position does not cover this shift">
        {row.ifApprovedCount}
        <span className="block text-[10px] text-neutral-500">Not this position</span>
      </span>
    );
  }
  return <span>{row.ifApprovedCount}</span>;
}

function ScheduleTable({ schedule, weekday }: { schedule: HolidayCoverageSchedule; weekday: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-neutral-800">{schedule.scheduleName}</div>
      <p className="text-[11px] text-neutral-500 leading-snug">{scheduleNote(schedule, weekday)}</p>
      {schedule.rows.length === 0 ? (
        <p className="text-xs text-neutral-500">No usual or scheduled shifts for this day.</p>
      ) : (
        <div className="overflow-x-auto border border-neutral-200 rounded-md">
          <table className="min-w-full text-xs">
            <thead className="bg-neutral-50 text-neutral-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Shift</th>
                <th className="px-2 py-1.5 text-right font-medium">Usual shifts</th>
                <th className="px-2 py-1.5 text-right font-medium">Scheduled shifts</th>
                <th className="px-2 py-1.5 text-right font-medium">Employees available</th>
                <th className="px-2 py-1.5 text-right font-medium">If approved</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {schedule.rows.map((row) => (
                <tr key={row.shiftLabel} className={row.shortBecauseOfApproval ? 'bg-amber-50/70' : ''}>
                  <td className="px-2 py-1.5 text-neutral-800">
                    {row.shiftLabel}
                    {row.applicantOnShift && (
                      <span className="block text-[10px] text-neutral-500">Already on this shift</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatUsual(row)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {row.scheduledCount == null ? '—' : row.scheduledCount}
                  </td>
                  <td
                    className="px-2 py-1.5 text-right tabular-nums"
                    title={row.availableNames.length > 0 ? row.availableNames.join(', ') : undefined}
                  >
                    {row.availableCount}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <IfApprovedCell row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function HolidayCoverageMatrix({
  result,
  loading,
  error,
}: {
  result?: HolidayCoverageEmployee;
  loading: boolean;
  error: string | null;
}) {
  if (loading && !result) {
    return <p className="text-xs text-neutral-500">Loading shift coverage…</p>;
  }
  if (error && !result) {
    return <p className="text-xs text-red-600">{error}</p>;
  }
  if (!result) return null;

  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 space-y-3">
      <div className="text-sm font-semibold text-neutral-800">Coverage if this leave is approved</div>
      {result.message && <p className="text-xs text-neutral-600">{result.message}</p>}
      {result.schedules.map((schedule) => (
        <ScheduleTable key={schedule.scheduleId} schedule={schedule} weekday={result.weekday} />
      ))}
    </div>
  );
}
